// --- React
import React, { useState } from "react";
// --- External UI Libraries
import Button from "@dtci/spellbook/Button";
import { MultiSelect } from "@dtci/spellbook/MultiSelect";
// --- Styles
import "./styles.scss";

/**
 * Filters Sidebar Component (Always Visible)
 *
 * Features:
 * - Persistent right sidebar for filters
 * - Can be collapsed to just an icon
 * - Active filter counts
 */
export function FiltersSidebar({
  filterColumns,
  filterOptions,
  internalFilters,
  onFilterChange,
  onClearFilters,
  activeFilterCount,
}) {
  const [collapsed, setCollapsed] = useState(false);

  /** Render a filter field */
  const renderFilterField = (col) => {
    const options = filterOptions?.[col.field] || [];
    const activeValues = internalFilters?.[col.field] || [];

    return (
      <div key={col.field} className="sidebar-filter-field">
        <label>{col.label}</label>
        <MultiSelect
          options={options}
          isClearable
          isSearchable
          onChange={(values) => onFilterChange(col.field, values)}
          value={activeValues}
          placeholder="All"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (base) => ({
              ...base,
              minHeight: "32px",
              fontSize: "13px",
            }),
            valueContainer: (base) => ({
              ...base,
              flexWrap: "wrap",
              padding: "2px 6px",
            }),
            multiValue: (base) => ({
              ...base,
              margin: "1px",
              fontSize: "12px",
            }),
            menu: (base) => ({ ...base, minWidth: "200px" }),
            placeholder: (base) => ({ ...base, fontSize: "13px" }),
          }}
        />
      </div>
    );
  };

  if (collapsed) {
    return (
      <div className="filters-sidebar filters-sidebar-collapsed">
        <button
          className="sidebar-toggle"
          onClick={() => setCollapsed(false)}
          title="Expand filters"
        >
          <i className="fa-regular fa-chevron-left" />
          {activeFilterCount > 0 && (
            <span className="toggle-badge">{activeFilterCount}</span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="filters-sidebar">
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-title">
          <i className="fa-regular fa-filter" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="total-badge">{activeFilterCount}</span>
          )}
        </div>
        <button
          className="sidebar-toggle"
          onClick={() => setCollapsed(true)}
          title="Collapse sidebar"
        >
          <i className="fa-regular fa-chevron-right" />
        </button>
      </div>

      {/* Filter list */}
      <div className="sidebar-content">
        <div className="sidebar-filters-list">
          {filterColumns?.map(renderFilterField)}
        </div>
      </div>

      {/* Footer */}
      <div className="sidebar-footer">
        <Button
          sizeType="small"
          styleType="standard"
          onClick={onClearFilters}
          disabled={!activeFilterCount}
          className="clear-btn"
        >
          Clear All
        </Button>
      </div>
    </div>
  );
}
